import { useMemo } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { AnalyticsDestination, AnalyticsProvider } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  useCreateAnalyticsDestination,
  useUpdateAnalyticsDestination,
} from '../hooks/use-analytics-destination-mutations';
import {
  destinationFormSchema,
  emptyDestinationForm,
  fromDestination,
  hasTestEventCode,
  toDestinationCreate,
  toDestinationUpdate,
  type DestinationForm,
} from '../schemas/destination-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'provider',
  'config.pixelId',
  'config.testEventCode',
  'config.measurementId',
  'config.pixelCode',
  'config.adAccountId',
  'config.testMode',
  'credential',
]);

const IDENTIFIER_FIELD = {
  meta_capi: { name: 'config.pixelId', inputMode: 'numeric', placeholder: undefined },
  ga4_mp: { name: 'config.measurementId', inputMode: undefined, placeholder: 'G-A1B2C3D4' },
  tiktok_events: {
    name: 'config.pixelCode',
    inputMode: undefined,
    placeholder: 'C4A1B2C3D4E5F6G7H8I9',
  },
  pinterest_capi: { name: 'config.adAccountId', inputMode: 'numeric', placeholder: undefined },
} as const satisfies Record<
  AnalyticsProvider,
  {
    name: `config.${'pixelId' | 'measurementId' | 'pixelCode' | 'adAccountId'}`;
    inputMode: 'numeric' | undefined;
    placeholder: string | undefined;
  }
>;

/** The selected provider's identifier, its test event code or test mode where it has one. */
function ProviderFields({
  form,
  provider,
  focusCredential,
}: {
  form: UseFormReturn<DestinationForm>;
  provider: AnalyticsProvider;
  focusCredential: boolean;
}) {
  const { t } = useTranslation('analytics');
  const identifier = IDENTIFIER_FIELD[provider];
  const key = identifier.name.slice('config.'.length);
  return (
    <>
      <FormField
        key={identifier.name}
        control={form.control}
        name={identifier.name}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t(`fields.${key}`)}</FormLabel>
            <FormControl>
              <Input
                inputMode={identifier.inputMode}
                autoComplete="off"
                spellCheck={false}
                className="font-mono text-[13px]"
                placeholder={identifier.placeholder}
                data-autofocus={focusCredential ? undefined : ''}
                {...field}
              />
            </FormControl>
            <FormDescription>{t(`fields.${key}Hint`)}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      {hasTestEventCode(provider) ? (
        <FormField
          control={form.control}
          name="config.testEventCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.testEventCode')}</FormLabel>
              <FormControl>
                <Input autoComplete="off" className="font-mono text-[13px]" {...field} />
              </FormControl>
              <FormDescription>{t(`fields.testEventCodeHint.${provider}`)}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      {provider === 'pinterest_capi' ? (
        <FormField
          control={form.control}
          name="config.testMode"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start gap-3">
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
              </FormControl>
              <div className="grid gap-1">
                <FormLabel>{t('fields.testMode')}</FormLabel>
                <FormDescription>{t('fields.testModeHint')}</FormDescription>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
      ) : null}
    </>
  );
}

export interface DestinationDialogProps {
  storeId: string;
  /** Null connects a new destination. */
  destination: AnalyticsDestination | null;
  /** Providers offered when connecting: the ones the store has no destination for. */
  available: readonly AnalyticsProvider[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens an existing destination with the credential field focused, to replace it. */
  focusCredential?: boolean;
}

function DestinationFormBody({
  storeId,
  destination,
  available,
  focusCredential,
  onClose,
}: {
  storeId: string;
  destination: AnalyticsDestination | null;
  available: readonly AnalyticsProvider[];
  focusCredential: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(['analytics', 'common']);
  const message = useErrorMessage();
  const create = useCreateAnalyticsDestination(storeId);
  const update = useUpdateAnalyticsDestination(storeId);
  const pending = create.isPending || update.isPending;
  const mode = destination ? 'edit' : 'create';
  const schema = useMemo(() => destinationFormSchema(mode), [mode]);
  const form = useForm<DestinationForm>({
    resolver: useI18nResolver(schema),
    defaultValues: destination
      ? fromDestination(destination)
      : emptyDestinationForm(available[0] ?? 'meta_capi'),
  });
  const provider = useWatch({ control: form.control, name: 'provider' });

  const submit = form.handleSubmit(async (values) => {
    try {
      if (destination) {
        await update.mutateAsync({ id: destination.id, body: toDestinationUpdate(values) });
      } else {
        await create.mutateAsync(toDestinationCreate(values));
      }
      onClose();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        {destination ? null : (
          <FormField
            control={form.control}
            name="provider"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('analytics:fields.provider')}</FormLabel>
                <FormControl>
                  <RadioGroup value={field.value} onValueChange={field.onChange} className="gap-2">
                    {available.map((option) => (
                      <label
                        key={option}
                        htmlFor={`destination-provider-${option}`}
                        className={cn(
                          'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                          field.value === option && 'border-primary bg-primary-tint/40',
                        )}
                      >
                        <RadioGroupItem
                          id={`destination-provider-${option}`}
                          value={option}
                          className="mt-0.5"
                        />
                        <span className="grid gap-0.5 text-sm">
                          <span className="font-medium">{t(`analytics:providers.${option}`)}</span>
                          <span className="text-muted-foreground text-xs">
                            {t(`analytics:providerHints.${option}`)}
                          </span>
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <ProviderFields form={form} provider={provider} focusCredential={focusCredential} />
        <FormField
          control={form.control}
          name="credential"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {destination
                  ? t(`analytics:fields.newCredential.${provider}`)
                  : t(`analytics:fields.credential.${provider}`)}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  showLabel={t('common:password.showSecret')}
                  hideLabel={t('common:password.hideSecret')}
                  autoComplete="new-password"
                  spellCheck={false}
                  className="font-mono text-[13px]"
                  data-autofocus={focusCredential ? '' : undefined}
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {destination
                  ? t('analytics:fields.newCredentialHint')
                  : t(`analytics:fields.credentialHint.${provider}`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending
              ? t('common:actions.saving')
              : destination
                ? t('analytics:edit.submit')
                : t('analytics:create.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Connects a platform or edits one; the credential is write-only in both. */
export function DestinationDialog({
  storeId,
  destination,
  available,
  open,
  onOpenChange,
  focusCredential = false,
}: DestinationDialogProps) {
  const { t } = useTranslation('analytics');
  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg"
        onOpenAutoFocus={(event) => {
          // Radix would focus the first field; replacing a credential starts on the credential.
          const target = event.currentTarget;
          if (!(target instanceof HTMLElement)) return;
          const field = target.querySelector<HTMLElement>('[data-autofocus]');
          if (!field) return;
          event.preventDefault();
          field.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {destination
              ? t('edit.title', { provider: t(`providers.${destination.provider}`) })
              : t('create.title')}
          </DialogTitle>
          <DialogDescription>
            {destination ? t('edit.description') : t('create.description')}
          </DialogDescription>
        </DialogHeader>
        <DestinationFormBody
          storeId={storeId}
          destination={destination}
          available={available}
          focusCredential={focusCredential}
          onClose={close}
        />
      </DialogContent>
    </Dialog>
  );
}
